import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { post, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/Auth";
import { Logo, Btn } from "@/components/kit";

export function OtpLogin({ portal = "customer", onDone, label = "Mobile number" }) {
  const [ident, setIdent] = useState("");
  const [otp, setOtp] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await post("/auth/send-otp", { identifier: ident }); setSent(r.data); toast.success("OTP sent"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const verify = async () => {
    setBusy(true);
    try { const r = await post("/auth/verify-otp", { identifier: ident, otp, portal }); onDone(r.data.user); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
        <input data-testid={`${portal}-otp-identifier`} className="fo-input" value={ident} onChange={(e) => setIdent(e.target.value.trim())} placeholder={portal === "customer" ? "10-digit mobile" : "Mobile or email"} disabled={!!sent} />
      </label>
      {sent && (
        <>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Enter 6-digit OTP</span>
            <input data-testid={`${portal}-otp-input`} className="fo-input tracking-[0.4em]" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} />
          </label>
          {sent.dev_otp && <p data-testid="dev-otp-hint" className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">DEMO MODE (mock SMS): your OTP is <b className="font-mono">{sent.dev_otp}</b></p>}
        </>
      )}
      {!sent ? <Btn data-testid={`${portal}-send-otp-btn`} className="w-full" loading={busy} onClick={send}>Send OTP</Btn>
        : <div className="flex gap-2"><Btn variant="outline" onClick={() => { setSent(null); setOtp(""); }}>Change</Btn><Btn data-testid={`${portal}-verify-otp-btn`} className="flex-1" loading={busy} onClick={verify}>Verify & Continue</Btn></div>}
    </div>
  );
}

function PasswordLogin({ onDone }) {
  const [f, setF] = useState({ identifier: "", password: "" });
  const [ch, setCh] = useState(null);
  const [otp, setOtp] = useState("");
  const [forgot, setForgot] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await post("/auth/login", f);
      if (r.data.requires_2fa) setCh(r.data); else onDone(r.data.user);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  const verify2fa = async () => {
    try { const r = await post("/auth/login/2fa", { challenge_id: ch.challenge_id, otp }); onDone(r.data.user); } catch (e) { toast.error(errMsg(e)); }
  };
  const doForgot = async () => {
    try { const r = await post("/auth/forgot-password", { identifier: f.identifier }); setForgot({ token: r.data.dev_reset_token || "", pw: "" }); toast.success(r.message); } catch (e) { toast.error(errMsg(e)); }
  };
  const doReset = async () => {
    try { await post("/auth/reset-password", { token: forgot.token, new_password: forgot.pw }); toast.success("Password reset. Please login."); setForgot(null); } catch (e) { toast.error(errMsg(e)); }
  };
  if (ch) return (
    <div className="space-y-3" data-testid="twofa-step">
      <p className="text-sm text-slate-600">Two-factor authentication: enter the OTP sent to your email.</p>
      {ch.dev_otp && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">DEMO MODE: OTP <b className="font-mono">{ch.dev_otp}</b></p>}
      <input data-testid="twofa-otp-input" className="fo-input" value={otp} onChange={(e) => setOtp(e.target.value)} />
      <Btn data-testid="twofa-verify-btn" className="w-full" onClick={verify2fa}>Verify</Btn>
    </div>
  );
  if (forgot) return (
    <div className="space-y-3" data-testid="reset-step">
      <p className="text-xs text-slate-500">Paste the reset token (sent to your email; shown here in DEMO mode) and set a new password.</p>
      <input data-testid="reset-token-input" className="fo-input font-mono text-xs" value={forgot.token} onChange={(e) => setForgot({ ...forgot, token: e.target.value })} placeholder="Reset token" />
      <input data-testid="reset-password-input" type="password" className="fo-input" value={forgot.pw} onChange={(e) => setForgot({ ...forgot, pw: e.target.value })} placeholder="New password (8+ chars, letters & numbers)" />
      <div className="flex gap-2"><Btn variant="outline" onClick={() => setForgot(null)}>Back</Btn><Btn data-testid="reset-submit-btn" className="flex-1" onClick={doReset}>Reset password</Btn></div>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3">
      <input data-testid="login-identifier-input" className="fo-input" placeholder="Email or mobile" value={f.identifier} onChange={(e) => setF({ ...f, identifier: e.target.value })} />
      <input data-testid="login-password-input" type="password" className="fo-input" placeholder="Password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <Btn data-testid="login-submit-btn" type="submit" className="w-full" loading={busy}>Login</Btn>
      <button type="button" data-testid="forgot-password-btn" onClick={doForgot} className="text-xs text-blue-800 hover:underline">Forgot password? (enter email above)</button>
    </form>
  );
}

export default function Login() {
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") === "partner" ? "partner" : "customer");
  const [mode, setMode] = useState("password");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = (u) => { setUser(u); toast.success(`Welcome, ${u.name}`); nav(sp.get("next") || homeFor(u)); };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex justify-center"><Logo /></Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div className="mb-6 grid grid-cols-2 rounded-lg bg-slate-100 p-1 text-sm font-semibold">
            <button data-testid="tab-customer" onClick={() => setTab("customer")} className={`rounded-md py-2 ${tab === "customer" ? "bg-white shadow-sm" : "text-slate-500"}`}>Customer</button>
            <button data-testid="tab-partner" onClick={() => setTab("partner")} className={`rounded-md py-2 ${tab === "partner" ? "bg-white shadow-sm" : "text-slate-500"}`}>DSA / Employee</button>
          </div>
          {tab === "customer" ? (
            <>
              <h2 className="font-display text-xl font-bold">Login with mobile</h2>
              <p className="mb-4 text-sm text-slate-500">New customers are registered automatically.</p>
              <OtpLogin portal="customer" onDone={done} />
            </>
          ) : (
            <>
              <h2 className="font-display text-xl font-bold">Partner & staff login</h2>
              <div className="mb-4 mt-2 flex gap-4 text-xs font-semibold">
                <button data-testid="mode-password" onClick={() => setMode("password")} className={mode === "password" ? "text-blue-900 underline" : "text-slate-500"}>Password</button>
                <button data-testid="mode-otp" onClick={() => setMode("otp")} className={mode === "otp" ? "text-blue-900 underline" : "text-slate-500"}>OTP</button>
              </div>
              {mode === "password" ? <PasswordLogin onDone={done} /> : <OtpLogin portal="staff" label="Registered mobile or email" onDone={done} />}
              <p className="mt-5 text-center text-xs text-slate-500">New channel partner? <Link to="/partner/register" className="font-semibold text-blue-800" data-testid="register-dsa-link">Register as DSA</Link></p>
            </>
          )}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-400">Protected by OTP rate-limiting, session tracking and audit logs.</p>
      </div>
    </div>
  );
}
